/**
 * Ingredients — pilot 2 of the resource-kit v2 migration (#1800).
 *
 * See `../suppliers/schema.tsx` for why these two resources go first: both are
 * pure static CRUD that trips none of the bridge's fail-loud rules, and neither
 * uses any of the four capabilities the parity manifest (§5) parks on the host
 * (bulk actions, list total adornment, background refetch, nested `listPath`).
 * Moved onto the full `defineListView` + `defineFormView` contracts in #1805.
 */
import {
  type EnumOption,
  createFieldConstructors,
  defineFormView,
  defineListView,
  defineModel,
} from '@resource-kit/model';
import type { ResourceBundle } from '@resource-kit/controllers/react';
import type { Ingredient } from '@/services';
import { ingredientAPI, toListResult } from '@/services';
import { CANONICAL_UNITS, INGREDIENT_CATEGORIES } from '@/services/catalog-enums';
import { CATALOG_INGREDIENT_FILTERS, CATALOG_INGREDIENT_SORTS } from '@/services/filters.generated';
import { PERMISSIONS } from '@/permissions';

const storageOptions: EnumOption[] = [
  { value: 'ambient', label: 'ingredient.enum.storage.ambient' },
  { value: 'chilled', label: 'ingredient.enum.storage.chilled', color: 'primary' },
  { value: 'frozen', label: 'ingredient.enum.storage.frozen', color: 'secondary' },
];

// `unit` + `category` enums mirror the backend's closed-set arrays
// (issue #80) via the `catalog-enums.ts` bridge. The cross-repo
// sync test asserts they stay in lock-step (#83 method B).
const unitOptions: EnumOption[] = CANONICAL_UNITS.map((u) => ({
  value: u,
  label: `ingredient.enum.unit.${u}`,
}));

const categoryOptions: EnumOption[] = INGREDIENT_CATEGORIES.map((c) => ({
  value: c,
  label: `ingredient.enum.category.${c}`,
}));

const f = createFieldConstructors<Ingredient>();

export const ingredientModel = defineModel<Ingredient>()({
  name: 'ingredient',
  idField: 'id',
  recName: 'name',
  fields: {
    id: f.id({ label: 'ingredient.field.id' }),
    name: f.text({ label: 'ingredient.field.name' }),
    unit: f.enum({ label: 'ingredient.field.unit', options: unitOptions }),
    category: f.enum({ label: 'ingredient.field.category', options: categoryOptions }),
    storage: f.enum({ label: 'ingredient.field.storage', options: storageOptions }),
    shelf_life_days: f.number({ label: 'ingredient.field.shelfLifeDays' }),
    safety_stock: f.text({ label: 'ingredient.field.safetyStock' }),
    // The pilot many2one (#1804 batch C): the form slot resolves this id
    // against the supplier model instead of printing it. Registration of the
    // target lives in `src/features/relations.ts`.
    default_supplier_id: f.id({
      label: 'ingredient.field.defaultSupplierId',
      relation: 'supplier',
    }),
    photo_url: f.text({ label: 'ingredient.field.photoUrl' }),
    notes: f.text({ label: 'ingredient.field.notes' }),
    created_at: f.datetime({ label: 'ingredient.field.createdAt' }),
    updated_at: f.datetime({ label: 'ingredient.field.updatedAt' }),
  },
});

// See `../suppliers/schema.tsx` for why the modifiers live on the LIST items
// and the form tabs stay name-only, and why `capabilities.search` is absent.
export const ingredientListView = defineListView(ingredientModel, {
  items: [
    { field: 'id', columnInvisible: true, invisible: true },
    { field: 'name' },
    { field: 'unit' },
    { field: 'category' },
    { field: 'storage' },
    { field: 'shelf_life_days' },
    { field: 'safety_stock', columnInvisible: true },
    { field: 'default_supplier_id', columnInvisible: true },
    { field: 'photo_url', columnInvisible: true },
    { field: 'notes', columnInvisible: true },
    { field: 'created_at', columnInvisible: true, invisible: true },
    { field: 'updated_at', columnInvisible: true, invisible: true },
  ],
  defaultSort: { field: 'created_at', dir: 'desc' },
});

// Every EDITABLE field lives in the first tab (#1922). This resource had the
// worst of it: under the #1918 tab default its nine writable fields were split
// across THREE rail triggers, because `meta` carried `photo_url` and `notes`
// alongside the two audit timestamps. `meta` now holds only the two
// `serverAssigned` columns, which is what the name claimed all along.
export const ingredientFormView = defineFormView(ingredientModel, {
  groupDisplay: 'tab',
  children: [
    {
      kind: 'fields',
      id: 'identity',
      label: 'ingredient.group.identity',
      items: [
        { field: 'name' },
        { field: 'unit' },
        { field: 'category' },
        { field: 'storage' },
        { field: 'shelf_life_days' },
        { field: 'safety_stock' },
        { field: 'default_supplier_id' },
        { field: 'photo_url' },
        { field: 'notes' },
      ],
    },
    {
      kind: 'fields',
      id: 'meta',
      label: 'ingredient.group.meta',
      // `id` sits with the other server-assigned stamps rather than in
      // `identity`: it is a read-only UUID, and `identity` is the edit form.
      items: [{ field: 'id' }, { field: 'created_at' }, { field: 'updated_at' }],
    },
  ],
});

export const ingredientResource: ResourceBundle<Ingredient, typeof ingredientModel.fields> = {
  model: ingredientModel,
  list: ingredientListView,
  form: ingredientFormView,
  host: {
    api: {
      // The list page passes straight through (#1909); only the unwrapped
      // detail record still needs the legacy `ApiResponse` bridge (#1151).
      list: async (params) => toListResult(await ingredientAPI.list(params)),
      get: (id) => ingredientAPI.detail(id),
      create: (body) => ingredientAPI.create(body),
      update: (id, body) => ingredientAPI.update(id, body),
      delete: (id) => ingredientAPI.delete(id),
    },
    label: 'ingredient.label',
    slug: 'catalog-ingredients',
    listTitle: 'ingredient.listTitle',
    listDescription: 'ingredient.listDescription',
    // ingredients re-gated onto `catalog:read` — the backend folded the
    // legacy `ingredients:*` keys into the unified `catalog:*` module (#943).
    permission: {
      list: PERMISSIONS.CATALOG_READ,
      read: PERMISSIONS.CATALOG_READ,
      // Mutations require `catalog:write` (backend write routes enforce it);
      // gating the buttons here stops a read-only viewer seeing a control
      // the server would only 403 — the repo's forbidden deceptive-affordance.
      create: PERMISSIONS.CATALOG_WRITE,
      update: PERMISSIONS.CATALOG_WRITE,
      delete: PERMISSIONS.CATALOG_WRITE,
    },
    // EMPTY on purpose (#2114). `/api/catalog/ingredients` declares exactly
    // `search`/`page`/`size` — no filter parameter of any kind — so nothing on
    // this list is filterable, and the generated constant says so.
    //
    // OMITTING this key is NOT the same statement: `undefined` reads as
    // "unrestricted", and `deriveQueryColumnsFromFields` then hands the
    // QueryBuilder every projectable field with the full operator registry.
    // Measured through `ResourcePage` before this line existed: the field menu
    // offered all TWELVE columns, and `toApiListParams` dropped every one of
    // them before the request — with no warning either, since
    // `droppedFilterFields` returns [] when there is no whitelist to check
    // against. `{}` deletes those twelve lying columns and turns the silent
    // drop into a visible, machine-checked one.
    //
    // Bound by reference, never as a `{}` literal, so regeneration flows
    // through (#1837 guardrail A).
    filterable: CATALOG_INGREDIENT_FILTERS,
    // The same seam, one face over (#2114). `/api/catalog/ingredients`
    // declares `x-sortable: []` and its handler never reads a sort param, but
    // an ABSENT declaration made `createColumns` mark every header sortable.
    // Losing those headers is the point: each one issued a sort the route
    // ignores, which on screen is indistinguishable from a sort that ran.
    sortable: CATALOG_INGREDIENT_SORTS,
    searchPlaceholder: 'ingredient.searchPlaceholder',
    formConfig: {
      createTitle: 'ingredient.form.createTitle',
      editTitle: 'ingredient.form.editTitle',
      columns: 2,
      maxWidth: 'md',
    },
  },
};
